import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { Building2, ChevronRight } from 'lucide-react'
import { listarMunicipios } from '@/api/auth'
import { describirError } from '@/api/cliente'
import { actualizarMunicipios } from '@/sesion/almacen'
import { useSesion } from '@/sesion/useSesion'
import { EstadoCargando, EstadoError, EstadoVacio } from '@/componentes/Estados'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import BarraUsuario from '@/layout/BarraUsuario'

/**
 * El selector de municipio. Solo se ve cuando el usuario tiene mas de uno (o
 * ninguno): con uno solo, el login entra derecho a su panel.
 */
export default function ElegirMunicipio() {
  const { sesion } = useSesion()

  const consulta = useQuery({
    queryKey: ['municipios'],
    queryFn: listarMunicipios,
    retry: false,
  })

  const datos = consulta.data

  useEffect(() => {
    if (datos !== undefined) {
      actualizarMunicipios(datos.data)
    }
  }, [datos])

  const municipios = datos?.data ?? sesion?.municipios ?? []

  return (
    <div className="min-h-dvh bg-muted/40">
      <BarraUsuario />
      <main className="mx-auto w-full max-w-2xl p-6">
        <h1 className="text-xl font-semibold">Elige un municipio</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Trabajas en más de una comuna. Elige con cuál quieres seguir; puedes cambiar cuando
          quieras desde la cabecera.
        </p>

        {consulta.isPending && municipios.length === 0 && (
          <EstadoCargando etiqueta="Cargando tus municipios…" filas={2} />
        )}

        {consulta.isError && municipios.length === 0 && (
          <EstadoError
            error={describirError(consulta.error)}
            alReintentar={() => void consulta.refetch()}
            reintentando={consulta.isFetching}
          />
        )}

        {!consulta.isPending && !consulta.isError && municipios.length === 0 && (
          <EstadoVacio
            titulo="Tu cuenta no tiene municipios asignados"
            detalle="Pídele al administrador del sistema que te habilite en la comuna donde vas a trabajar."
          />
        )}

        <ul className="space-y-3">
          {municipios.map((municipio) => (
            <li key={municipio.slug}>
              <Link to={`/m/${municipio.slug}`} className="block rounded-xl outline-none focus-visible:ring-3 focus-visible:ring-ring/50">
                <Card className="transition-colors hover:border-ring">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="size-4 text-muted-foreground" aria-hidden="true" />
                      {municipio.nombre}
                      <ChevronRight className="ml-auto size-4 text-muted-foreground" aria-hidden="true" />
                    </CardTitle>
                    <CardDescription>{municipio.slug}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-wrap gap-2">
                    {municipio.roles.map((rol) => (
                      <Badge key={rol} variant="secondary">
                        {rol}
                      </Badge>
                    ))}
                  </CardContent>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </div>
  )
}
